// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-empty-object-type, @typescript-eslint/no-explicit-any, react/prop-types */

import type { Layer } from "@deck.gl/core";
import _ from "lodash";
import React, { createContext, useCallback, useContext, useReducer } from "react";
import type { ColorStop } from "../MapStyles";

interface ViewerState {
	// Layer visibility
	orthoVisible: boolean;
	heatmapVisible: boolean;
	dataLayerVisible: boolean;
	plotsVisible: boolean;
	plotLabelsVisible: "name" | "heatmap" | "none";
	treatmentsVisible: boolean;
	statusMarkersVisible: boolean;

	// Sources
	viewerMapboxUploads: { uploadId: string; tilesetId: string }[];
	dataLayerGeometryUrl?: string;
	plotAssociatedGeometryUrl?: string;
	// deck.gl
	deckLayers: Layer[];

	// TODO: make fully local
	selectedOrthoFlight?: string;
	selectedHeatmapFlightDate?: string;

	// Layer styling
	heatmapColorStops: ColorStop[];
	heatmapDomain: [number, number];
	heatmapOpacity: number;
	legendStatus: { [key: string]: boolean | "legendonly" };
	selectedPlotIds: string[];
	selectedGroup: number | null;
	layerOrder: string[];
	dataLayerColorMap: string[];
	dataLayerOpacity: number;

	// Viewer data
	dataLayerGroupMap: { [plotId: string]: number };
	experimentalDesign: {
		plotId: string;
		treatment: number;
		plotGroup: number;
		replicate: number;
	}[];
	treatmentNames: string[];
}

interface ViewerFunctions {
	updateOrthoVisible: (visible: boolean) => void;
	updateHeatmapVisible: (visible: boolean) => void;
	updateDataLayerVisible: (visible: boolean) => void;
	updatePlotsVisible: (visible: boolean) => void;
	updatePlotLabelsVisible: (visible: "name" | "heatmap" | "none") => void;
	updateTreatmentsVisible: (visible: boolean) => void;
	updateStatusMarkersVisible: (visible: boolean) => void;
	updateViewerMapboxUploads: (
		uploads: {
			uploadId: string;
			tilesetId: string;
		}[],
	) => void;
	updateDataLayerGeometryUrl: (dataLayerGeometryUrl?: string) => void;
	updatePlotAssociatedGeometryUrl: (plotAssociatedGeometryUrl?: string) => void;
	updateDeckLayers: (layers: Layer<{}>[]) => void;
	updateHeatmapColorStops: (stops: ColorStop[]) => void;
	updateHeatmapDomain: (domain: [number, number]) => void;
	updateHeatmapOpacity: (opactiy: number) => void;
	updateLegendStatus: (legendStatus: { [key: string]: boolean | "legendonly" }) => void;
	updateSelectedPlotIds: (ids: string[]) => void;
	updateSelectedGroup: (id: number | null) => void;
	updateLayerOrder: (layers: string[]) => void;
	updateDataLayerColorMap: (color: string[]) => void;
	updateDataLayerOpacity: (opactiy: number) => void;
	updateDataLayerGroupMap: (map: { [plotId: string]: number }) => void;
	updateSelectedOrthoFlight: (flight: string | undefined) => void;
	updateExperimentalDesign: (
		design: {
			plotId: string;
			treatment: number;
			plotGroup: number;
			replicate: number;
		}[],
	) => void;
	updateTreatmentNames: (treatmentNames: string[]) => void;
}

type ViewerAction =
	| {
			type: "visibility";
			options: {
				orthoVisible?: boolean;
				heatmapVisible?: boolean;
				dataLayerVisible?: boolean;
				plotsVisible?: boolean;
				plotLabelsVisible?: "name" | "heatmap" | "none";
				treatmentsVisible?: boolean;
				statusMarkersVisible?: boolean;
			};
	  }
	| {
			type: "source";
			sources: {
				viewerMapboxUploads?: { uploadId: string; tilesetId: string }[];
				dataLayerGeometryUrl?: string;
				plotAssociatedGeometryUrl?: string;
			};
	  }
	| {
			type: "styling";
			options: {
				heatmapColorStops?: ColorStop[];
				heatmapDomain?: [number, number];
				heatmapOpacity?: number;
				legendStatus?: { [key: string]: boolean | "legendonly" };
				selectedPlotIds?: string[];
				selectedGroup?: number | null;
				layerOrder?: string[];
				dataLayerColorMap?: string[];
				dataLayerOpacity?: number;
			};
	  }
	| { type: "deck.gl"; layers: Layer[] }
	| {
			type: "data";
			data: {
				dataLayerGroupMap?: { [plotId: string]: number };
				selectedOrthoFlight?: string;
				experimentalDesign?: {
					plotId: string;
					treatment: number;
					plotGroup: number;
					replicate: number;
				}[];
				treatmentNames?: string[];
			};
	  };

const checkPayloadIsNew = (state: ViewerState, payload: { [key: string]: any }) => {
	for (const key in payload) {
		if (key in state) {
			// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
			if (_.isEqual(state[key], payload[key])) {
				return false;
			}
		}
	}
	return true;
};

const viewerReducer = (state: ViewerState, action: ViewerAction) => {
	switch (action.type) {
		case "visibility": {
			if (checkPayloadIsNew(state, action.options)) {
				return { ...state, ...action.options };
			}
			return state;
		}
		case "source": {
			if (checkPayloadIsNew(state, action.sources)) {
				return { ...state, ...action.sources };
			}
			return state;
		}
		case "styling": {
			if (checkPayloadIsNew(state, action.options)) {
				return { ...state, ...action.options };
			}
			return state;
		}
		case "deck.gl": {
			if (checkPayloadIsNew(state, { deckLayers: action.layers })) {
				return { ...state, deckLayers: action.layers };
			}
			return state;
		}
		case "data": {
			if (checkPayloadIsNew(state, action.data)) {
				return { ...state, ...action.data };
			}
			return state;
		}
		default:
			return state;
	}
};

const ViewerContext = createContext<ViewerState | null>(null);
const ViewerDispatchContext = createContext<ViewerFunctions | null>(null);

// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
const ViewerProvider = ({ children }) => {
	const [viewer, dispatch] = useReducer(viewerReducer, {
		orthoVisible: true,
		heatmapVisible: true,
		dataLayerVisible: true,
		plotsVisible: true,
		plotLabelsVisible: "none",
		treatmentsVisible: true,
		statusMarkersVisible: true,
		viewerMapboxUploads: [],
		deckLayers: [],
		heatmapColorStops: [],
		heatmapDomain: [0, 1],
		heatmapOpacity: 0.65,
		legendStatus: {},
		selectedPlotIds: [],
		selectedGroup: null,
		dataLayerGroupMap: {},
		experimentalDesign: [],
		treatmentNames: [],
		layerOrder: [],
		dataLayerColorMap: ["#ffffff", "#000000"],
		dataLayerOpacity: 0.45,
	});

	const updateOrthoVisible = useCallback(
		(visible: boolean) => dispatch({ type: "visibility", options: { orthoVisible: visible } }),
		[],
	);
	const updateHeatmapVisible = useCallback(
		(visible: boolean) => dispatch({ type: "visibility", options: { heatmapVisible: visible } }),
		[],
	);
	const updateDataLayerVisible = useCallback(
		(visible: boolean) => dispatch({ type: "visibility", options: { dataLayerVisible: visible } }),
		[],
	);
	const updatePlotsVisible = useCallback(
		(visible: boolean) => dispatch({ type: "visibility", options: { plotsVisible: visible } }),
		[],
	);
	const updatePlotLabelsVisible = useCallback(
		(visible: "name" | "heatmap" | "none") => dispatch({ type: "visibility", options: { plotLabelsVisible: visible } }),
		[],
	);
	const updateTreatmentsVisible = useCallback(
		(visible: boolean) => dispatch({ type: "visibility", options: { treatmentsVisible: visible } }),
		[],
	);
	const updateStatusMarkersVisible = useCallback(
		(visible: boolean) => dispatch({ type: "visibility", options: { statusMarkersVisible: visible } }),
		[],
	);

	const updateViewerMapboxUploads = useCallback(
		(uploads: { uploadId: string; tilesetId: string }[]) =>
			dispatch({ type: "source", sources: { viewerMapboxUploads: uploads } }),
		[],
	);
	const updateDataLayerGeometryUrl = useCallback(
		(dataLayerGeometryUrl?: string) => dispatch({ type: "source", sources: { dataLayerGeometryUrl } }),
		[],
	);

	const updatePlotAssociatedGeometryUrl = useCallback(
		(plotAssociatedGeometryUrl?: string) => dispatch({ type: "source", sources: { plotAssociatedGeometryUrl } }),
		[],
	);

	const updateDeckLayers = useCallback((layers: Layer[]) => dispatch({ type: "deck.gl", layers }), []);

	const updateHeatmapColorStops = useCallback(
		(stops: ColorStop[]) => dispatch({ type: "styling", options: { heatmapColorStops: stops } }),
		[],
	);
	const updateHeatmapDomain = useCallback(
		(domain: [number, number]) => dispatch({ type: "styling", options: { heatmapDomain: domain } }),
		[],
	);
	const updateHeatmapOpacity = useCallback(
		(opacity: number) => dispatch({ type: "styling", options: { heatmapOpacity: opacity } }),
		[],
	);
	const updateLegendStatus = useCallback(
		(legendStatus: { [key: string]: boolean | "legendonly" }) =>
			dispatch({ type: "styling", options: { legendStatus } }),
		[],
	);
	const updateSelectedPlotIds = useCallback(
		(ids: string[]) => dispatch({ type: "styling", options: { selectedPlotIds: ids } }),
		[],
	);
	const updateSelectedGroup = useCallback(
		(id: number | null) => dispatch({ type: "styling", options: { selectedGroup: id } }),
		[],
	);
	const updateLayerOrder = useCallback(
		(layers: string[]) => dispatch({ type: "styling", options: { layerOrder: layers } }),
		[],
	);
	const updateDataLayerColorMap = useCallback(
		(colors: string[]) => dispatch({ type: "styling", options: { dataLayerColorMap: colors } }),
		[],
	);
	const updateDataLayerOpacity = useCallback(
		(opacity: number) => dispatch({ type: "styling", options: { dataLayerOpacity: opacity } }),
		[],
	);

	const updateDataLayerGroupMap = useCallback(
		(map: { [plotId: string]: number }) => dispatch({ type: "data", data: { dataLayerGroupMap: map } }),
		[],
	);
	const updateSelectedOrthoFlight = useCallback(
		(flight: string | undefined) => dispatch({ type: "data", data: { selectedOrthoFlight: flight } }),
		[],
	);
	const updateExperimentalDesign = useCallback(
		(
			design: {
				plotId: string;
				treatment: number;
				plotGroup: number;
				replicate: number;
			}[],
		) => dispatch({ type: "data", data: { experimentalDesign: design } }),
		[],
	);
	const updateTreatmentNames = useCallback(
		(treatmentNames: string[]) => dispatch({ type: "data", data: { treatmentNames } }),
		[],
	);

	return (
		<ViewerContext.Provider value={viewer}>
			<ViewerDispatchContext.Provider
				value={{
					updateOrthoVisible,
					updateHeatmapVisible,
					updateDataLayerVisible,
					updatePlotsVisible,
					updatePlotLabelsVisible,
					updateTreatmentsVisible,
					updateStatusMarkersVisible,
					updateViewerMapboxUploads,
					updateDataLayerGeometryUrl,
					updatePlotAssociatedGeometryUrl,
					updateDeckLayers,
					updateHeatmapColorStops,
					updateHeatmapDomain,
					updateHeatmapOpacity,
					updateLegendStatus,
					updateSelectedPlotIds,
					updateSelectedGroup,
					updateLayerOrder,
					updateDataLayerColorMap,
					updateDataLayerOpacity,
					updateDataLayerGroupMap,
					updateSelectedOrthoFlight,
					updateExperimentalDesign,
					updateTreatmentNames,
				}}
			>
				{children}
			</ViewerDispatchContext.Provider>
		</ViewerContext.Provider>
	);
};

const useViewer = () => {
	const context = useContext(ViewerContext);

	if (context === null) {
		throw new Error("ViewerContext is not provided");
	}

	return context;
};

const useViewerDispatch = () => {
	const context = useContext(ViewerDispatchContext);

	if (context === null) {
		throw new Error("ViewerDispatchContext is not provided");
	}

	return context;
};

export { useViewer, useViewerDispatch, ViewerProvider };
